// Simple Analytics Tracking for Vibe Coding Coach Beta
// Tracks events locally and sends to backend

const STORAGE_KEY = 'vibe-coach-analytics';
const SESSION_KEY = 'vibe-coach-session';
const DEBUG_KEY = 'vibe-coach-debug-session';
const USER_ID_KEY = 'vibe-coach-user-id';

// Check if this is a debug/test session (via URL parameter or stored flag)
const checkDebugMode = () => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true' || urlParams.get('test') === 'true') {
        sessionStorage.setItem(DEBUG_KEY, 'true');
        return true;
    }
    // Check stored flag
    return sessionStorage.getItem(DEBUG_KEY) === 'true';
};

// Get debug mode status
export const isDebugSession = () => {
    return checkDebugMode();
};

// Manually set debug mode (for testing)
export const setDebugMode = (enabled) => {
    if (enabled) {
        sessionStorage.setItem(DEBUG_KEY, 'true');
    } else {
        sessionStorage.removeItem(DEBUG_KEY);
    }
};

// Get or create session ID
const getSessionId = () => {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
        sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
};

// Get current user ID (if logged in)
export const getCurrentUserId = () => {
    return sessionStorage.getItem(USER_ID_KEY) || null;
};

// Set current user ID (call when user logs in)
export const setCurrentUserId = (userId) => {
    if (userId) {
        sessionStorage.setItem(USER_ID_KEY, userId);
        // Send session link event to backend
        linkSessionToUser(userId);
    } else {
        sessionStorage.removeItem(USER_ID_KEY);
    }
};

// Link current session to user (for attribution)
const linkSessionToUser = async (userId) => {
    const sessionId = getSessionId();
    try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${backendUrl}/api/analytics/link-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, userId }),
        });
    } catch (e) {
        // Silently fail
    }
};

// Get session blueprint count
export const getSessionBlueprintCount = () => {
    const count = sessionStorage.getItem('vibe-coach-blueprint-count');
    return count ? parseInt(count, 10) : 0;
};

// Increment session blueprint count
const incrementBlueprintCount = () => {
    const count = getSessionBlueprintCount() + 1;
    sessionStorage.setItem('vibe-coach-blueprint-count', count.toString());
    return count;
};

// Track an event
export const trackEvent = async (eventName, properties = {}) => {
    const isDebug = isDebugSession();
    const userId = getCurrentUserId();
    
    const event = {
        event: eventName,
        properties: {
            ...properties,
            sessionId: getSessionId(),
            userId: userId,
            isDebugSession: isDebug,
            isAnonymous: !userId,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
        }
    };

    // Log to console in development
    console.log('[Analytics]', eventName, properties, isDebug ? '(DEBUG SESSION)' : '');

    // Store locally
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        stored.push(event);
        // Keep only last 100 events locally
        if (stored.length > 100) {
            stored.shift();
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {
        console.warn('Failed to store analytics event locally:', e);
    }

    // Send to backend (fire and forget)
    try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        fetch(`${backendUrl}/api/analytics/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(event),
        }).catch(() => {}); // Silently fail
    } catch (e) {
        // Silently fail - analytics should never break the app
    }
};

// Specific tracking functions
export const trackBlueprintGenerated = (appName, ideaLength, fromSharedView = false) => {
    const blueprintCount = incrementBlueprintCount();
    trackEvent('blueprint_generated', {
        appName,
        ideaLength,
        sessionBlueprintCount: blueprintCount,
        fromSharedView,
    });
};

export const trackBuilderPromptCopied = (builder) => {
    trackEvent('builder_prompt_copied', {
        builder,
        sessionBlueprintCount: getSessionBlueprintCount(),
    });
};

export const trackBuilderSelected = (builder) => {
    trackEvent('builder_selected', {
        builder,
        sessionBlueprintCount: getSessionBlueprintCount(),
    });
};

// Track build option selected (for Build This App section)
export const trackBuildOptionSelected = (option, projectId = null) => {
    trackEvent('build_option_selected', {
        option, // emergent | chatgpt | developer
        projectId,
        sessionBlueprintCount: getSessionBlueprintCount(),
    });
};

export const trackBlueprintCopied = () => {
    trackEvent('blueprint_copied', {
        sessionBlueprintCount: getSessionBlueprintCount(),
    });
};

export const trackBlueprintDownloaded = (appName) => {
    trackEvent('blueprint_downloaded', {
        appName,
        sessionBlueprintCount: getSessionBlueprintCount(),
    });
};

export const trackExampleIdeaClicked = (exampleLabel) => {
    trackEvent('example_idea_clicked', {
        exampleLabel,
    });
};

// EMRG Integration tracking
export const trackEmrgButtonClicked = (appName, fromSharedView = false) => {
    trackEvent('emrg_button_clicked', {
        appName,
        sessionBlueprintCount: getSessionBlueprintCount(),
        fromSharedView,
    });
};

// Remix tracking
export const trackBlueprintRemixed = (originalIdea, fromSharedView = false) => {
    trackEvent('blueprint_remixed', {
        originalIdea: originalIdea?.substring(0, 50),
        sessionBlueprintCount: getSessionBlueprintCount(),
        fromSharedView,
    });
};

// Community blueprint view tracking
export const trackSharedBlueprintViewed = (shortId, appName) => {
    // Mark session as coming from a shared view
    sessionStorage.setItem('vibe-coach-from-shared-view', 'true');
    sessionStorage.setItem('vibe-coach-shared-blueprint-id', shortId);
    trackEvent('shared_blueprint_viewed', {
        shortId,
        appName,
    });
};

// Check if current session originated from a shared view
export const isFromSharedView = () => {
    return sessionStorage.getItem('vibe-coach-from-shared-view') === 'true';
};

// Clear shared view flag (call after user navigates away from shared view)
export const clearSharedViewFlag = () => {
    sessionStorage.removeItem('vibe-coach-from-shared-view');
    sessionStorage.removeItem('vibe-coach-shared-blueprint-id');
};

// Page engagement tracking
export const trackPageEngagement = (timeOnPage, actionsCount) => {
    trackEvent('page_engagement', {
        timeOnPageSeconds: Math.round(timeOnPage / 1000),
        actionsCount,
        sessionBlueprintCount: getSessionBlueprintCount(),
    });
};

// Track when user lands on the page
export const trackPageVisit = () => {
    trackEvent('page_visit', {
        referrer: document.referrer || 'direct',
        isReturningUser: getSessionBlueprintCount() > 0,
    });
};

// Get all stored events (for debugging)
export const getStoredEvents = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
        return [];
    }
};
